import { FeaturedContent } from 'rwl-shared/dist/models/neighborhood';

type Review = FeaturedContent['reviews'][number];

/**
 * Compare reviews by star rating (higher first)
 */
export const compareByRating = (a: Review, b: Review): number => {
    return b.rating - a.rating;
};

/**
 * Compare reviews by whether they have comments (with comments first)
 */
export const compareByHasComments = (a: Review, b: Review): number => {
    const aHasComments = a.message && a.message.length > 0 ? 1 : 0;
    const bHasComments = b.message && b.message.length > 0 ? 1 : 0;
    return bHasComments - aHasComments;
};

/**
 * Compare reviews by whether they have an author photo (with photo first)
 */
export const compareByHasPhoto = (a: Review, b: Review): number => {
    const aHasImage = a.authorPhotoUrl ? 1 : 0;
    const bHasImage = b.authorPhotoUrl ? 1 : 0;
    return bHasImage - aHasImage;
};

/**
 * Get safe timestamp from a date, returning 0 for invalid/missing dates
 */
export const getSafeTimestamp = (date: Date | string | undefined): number => {
    if (!date) return 0;
    const time =
        date instanceof Date ? date.getTime() : new Date(date).getTime();
    return isNaN(time) ? 0 : time;
};

/**
 * Compare reviews by date (most recent first)
 */
export const compareByDate = (a: Review, b: Review): number => {
    const aTime = getSafeTimestamp(a.postedAt);
    const bTime = getSafeTimestamp(b.postedAt);
    return bTime - aTime;
};

/**
 * Sort reviews by: 1) star rating desc, 2) has comments, 3) has photo, 4) date desc
 */
export const sortReviews = (
    reviews: FeaturedContent['reviews']
): FeaturedContent['reviews'] => {
    return [...reviews].sort((a, b) => {
        // 1. Star rating (higher first)
        const ratingDiff = compareByRating(a, b);
        if (ratingDiff !== 0) return ratingDiff;

        // 2. Has comments (reviews with comments first)
        const commentsDiff = compareByHasComments(a, b);
        if (commentsDiff !== 0) return commentsDiff;

        // 3. Has image/author photo (reviews with photo first)
        const photoDiff = compareByHasPhoto(a, b);
        if (photoDiff !== 0) return photoDiff;

        // 4. Review date descending (most recent first)
        return compareByDate(a, b);
    });
};
